import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/Card';
import { Button } from '../components/Button';
import { KPICard } from '../components/KPICard';
import { ArrowLeft, Trophy, Activity, Target, Clock, CheckCircle, TrendingUp } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line } from 'recharts';
import { getPerson, getUnit, scoreColor, KPI_FORMULA, personTrend } from '../data/kpiIntelligence';
export default function KPIPersonnelDetail() {
  const {
    personId
  } = useParams();
  const navigate = useNavigate();
  const [trendPeriod, setTrendPeriod] = useState<'7h' | '30h' | '90h' | '1th'>('30h');
  const person = getPerson(personId || '');
  if (!person) {
    return <div className="flex flex-col items-center justify-center h-full space-y-4" data-id="element-1705">
        <h2 className="text-xl font-mono text-muted-foreground" data-id="element-1706">
          Data Personel tidak ditemukan
        </h2>
        <Button onClick={() => navigate(-1)} variant="outline" data-id="element-1707">
          <ArrowLeft className="w-4 h-4 mr-2" data-id="element-1708" /> Kembali
        </Button>
      </div>;
  }
  const unit = getUnit(person.bindaKode, person.unitId);
  const trends = personTrend(person);
  return <div className="space-y-6 animate-in fade-in duration-500 max-w-7xl mx-auto pb-12" data-id="element-1709">
      <div className="flex items-center gap-4 border-b border-border/50 pb-4" data-id="element-1710">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/kpi/unit/${person.bindaKode}/${person.unitId}`)} className="hover:bg-primary/20 hover:text-primary" data-id="element-1711">
          <ArrowLeft className="w-5 h-5" data-id="element-1712" />
        </Button>
        <div className="flex items-center gap-4" data-id="element-1713">
          <img src={person.foto} alt={person.nama} className="w-12 h-12 rounded-full border border-border/50" data-id="element-1714" />
          <div data-id="element-1715">
            <h1 className="text-2xl font-heading font-bold tracking-tight text-foreground flex items-center gap-3" data-id="element-1716">
              {person.nama.toUpperCase()}
              <span className="text-sm font-mono text-primary bg-primary/10 px-2 py-1 rounded border border-primary/30" data-id="element-1717">
                {person.id}
              </span>
            </h1>
            <p className="text-muted-foreground font-mono text-xs mt-1 uppercase tracking-widest" data-id="element-1718">
              Level 4: Kinerja Personel // {person.pangkat} // {unit?.nama}
              // {unit?.nama}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-1719">
        <KPICard title="Skor KPI Personel" value={person.score} icon={Target} colorVar={scoreColor(person.score)} trend={{
        value: `${person.trend >= 0 ? '+' : ''}${person.trend}%`,
        label: 'vs Periode Lalu',
        isPositive: person.trend >= 0
      }} data-id="element-1720" />
        <KPICard title="Grade" value={`Grade ${person.grade}`} icon={Trophy} colorVar={scoreColor(person.score)} progress={{
        value: person.score,
        label: 'Pencapaian',
        target: '100'
      }} data-id="element-1721" />
        <KPICard title="Ranking BINDA" value={`#${person.rankBinda}`} icon={Activity} colorVar="var(--status-info)" data-id="element-1722" />
        <KPICard title="Ranking Nasional" value={`#${person.rankNasional}`} icon={Trophy} colorVar="var(--primary)" data-id="element-1723" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-1724">
        {/* Detail KPI */}
        <Card className="glass-panel border-border/50 lg:col-span-1" data-id="element-1725">
          <CardHeader className="border-b border-border/50 bg-card/50" data-id="element-1726">
            <CardTitle className="text-sm font-mono uppercase tracking-widest" data-id="element-1727">
              Detail Komponen KPI
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 space-y-4" data-id="element-1728">
            {KPI_FORMULA.map(f => {
            const val = person.components[f.key];
            return <div key={f.key} data-id="element-1729">
                  <div className="flex justify-between text-xs font-mono mb-1" data-id="element-1730">
                    <span className="text-muted-foreground" data-id="element-1731">{f.label}</span>
                    <span style={{
                  color: scoreColor(val)
                }} data-id="element-1732">
                      {val}
                    </span>
                  </div>
                  <div className="h-1.5 bg-black/50 rounded-full overflow-hidden border border-border/50" data-id="element-1733">
                    <div className="h-full shadow-[0_0_10px_currentColor]" style={{
                  width: `${val}%`,
                  backgroundColor: scoreColor(val),
                  color: scoreColor(val)
                }} data-id="element-1734"></div>
                  </div>
                </div>;
          })}

            <div className="pt-4 border-t border-border/30 grid grid-cols-2 gap-4" data-id="element-1735">
              <div data-id="element-1736">
                <div className="text-[10px] font-mono text-muted-foreground uppercase mb-1" data-id="element-1737">
                  Laporan Valid
                </div>
                <div className="text-lg font-bold flex items-center gap-2" data-id="element-1738">
                  <CheckCircle className="w-4 h-4 text-green-500" data-id="element-1739" />
                  {person.laporanValid}
                </div>
              </div>
              <div data-id="element-1740">
                <div className="text-[10px] font-mono text-muted-foreground uppercase mb-1" data-id="element-1741">
                  Respons Tugas
                </div>
                <div className="text-lg font-bold flex items-center gap-2" data-id="element-1742">
                  <Clock className="w-4 h-4 text-blue-500" data-id="element-1743" />
                  {person.responsTugas}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grafik Tren */}
        <Card className="glass-panel border-border/50 lg:col-span-2" data-id="element-1744">
          <CardHeader className="border-b border-border/50 bg-card/50 flex flex-row items-center justify-between" data-id="element-1745">
            <CardTitle className="text-sm font-mono uppercase tracking-widest flex items-center gap-2" data-id="element-1746">
              <TrendingUp className="w-4 h-4 text-primary" data-id="element-1747" /> Tren Kinerja
            </CardTitle>
            <div className="flex gap-2" data-id="element-1748">
              {(['7h', '30h', '90h', '1th'] as const).map(p => <button key={p} onClick={() => setTrendPeriod(p)} className={`text-[10px] font-mono px-2 py-1 rounded transition-colors ${trendPeriod === p ? 'bg-primary text-primary-foreground' : 'bg-background/50 text-muted-foreground hover:text-foreground'}`} data-id="element-1749">
                  {p.toUpperCase()}
                </button>)}
            </div>
          </CardHeader>
          <CardContent className="p-4 h-[300px]" data-id="element-1750">
            <ResponsiveContainer width="100%" height="100%" data-id="element-1751">
              <LineChart data={trends[trendPeriod]} margin={{
              top: 5,
              right: 30,
              left: 0,
              bottom: 5
            }} data-id="element-1752">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" vertical={false} data-id="element-1753" />
                <XAxis dataKey="label" stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 10,
                fontFamily: 'monospace'
              }} data-id="element-1754" />
                <YAxis domain={[50, 100]} stroke="rgba(255,255,255,0.5)" tick={{
                fontSize: 10,
                fontFamily: 'monospace'
              }} data-id="element-1755" />
                <Tooltip contentStyle={{
                backgroundColor: 'rgba(15, 18, 25, 0.9)',
                borderColor: 'rgba(255,255,255,0.1)',
                borderRadius: '4px'
              }} itemStyle={{
                color: scoreColor(person.score),
                fontFamily: 'monospace'
              }} data-id="element-1756" />
                <Line type="monotone" dataKey="score" name="Skor" stroke={scoreColor(person.score)} strokeWidth={2} dot={{
                r: 4,
                fill: scoreColor(person.score)
              }} data-id="element-1757" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>;
}